import { API, graphqlOperation } from 'aws-amplify';
import Cookies from 'universal-cookie';
import { validateSession } from '../graphql/mutations';

// Use separate localStorage keys for admin vs user portals to prevent cross-portal interference
const ADMIN_SESSION_TOKEN_KEY = 'eop_admin_session_token';
const USER_SESSION_TOKEN_KEY = 'eop_user_session_token';
const SESSION_CONFLICT_KEY = 'eop_session_conflict';
const HEARTBEAT_INTERVAL_MS = 60000; // Send heartbeat every 60 seconds of activity

/**
 * Determine which session token key to use.
 * If isAdmin is explicitly passed, use that. Otherwise detect from current path.
 * @param {boolean} [isAdmin] - Optional: explicitly specify admin (true) or user (false)
 */
function getSessionTokenKey(isAdmin) {
    // If explicitly specified, use that
    if (typeof isAdmin === 'boolean') {
        return isAdmin ? ADMIN_SESSION_TOKEN_KEY : USER_SESSION_TOKEN_KEY;
    }
    // Otherwise detect from path
    if (typeof window !== 'undefined' && window.location.pathname.startsWith('/admin')) {
        return ADMIN_SESSION_TOKEN_KEY;
    }
    return USER_SESSION_TOKEN_KEY;
}

/**
 * Store the session token after login.
 * @param {string} token - The session token
 * @param {string} [userID] - Optional userID to start session invalidation polling
 * @param {boolean} [isAdmin] - Optional: explicitly specify admin (true) or user (false)
 */
export function setSessionToken(token, userID, isAdmin) {
    localStorage.setItem(getSessionTokenKey(isAdmin), token);
    if (userID) {
        localStorage.setItem('eop_session_user_id', userID);
    }
    startSessionHeartbeat();

    // Start fast polling for session invalidation detection
    const resolvedUserID = userID || localStorage.getItem('eop_session_user_id') || _getUserIDFromCookie();
    if (resolvedUserID) {
        startSessionSubscription(resolvedUserID);
    } else {
        setTimeout(() => {
            const delayedUserID = localStorage.getItem('eop_session_user_id') || _getUserIDFromCookie();
            if (delayedUserID) {
                startSessionSubscription(delayedUserID);
            }
        }, 2000);
    }
}

function _getUserIDFromCookie() {
    try {
        const cookies = new Cookies();
        return cookies.get('user_token') || null;
    } catch (e) {
        return null;
    }
}

/**
 * Get the current session token.
 */
export function getSessionToken() {
    return localStorage.getItem(getSessionTokenKey());
}

// Auto-start heartbeat if session already exists (page refresh scenario)
if (typeof window !== 'undefined' && localStorage.getItem(getSessionTokenKey())) {
    // Delay to ensure module is fully loaded
    setTimeout(() => startSessionHeartbeat(), 0);
}

/**
 * Remove the session token (on logout or session termination).
 */
export function clearSessionToken() {
    localStorage.removeItem(getSessionTokenKey());
    localStorage.removeItem('eop_session_user_id');
    stopSessionHeartbeat();
    stopSessionSubscription();
}

/**
 * Set the session conflict flag with a message.
 * This is read by the login page to show the conflict message.
 */
export function setSessionConflict(message) {
    localStorage.setItem(SESSION_CONFLICT_KEY, message);
}

/**
 * Get and clear the session conflict message (one-time read).
 */
export function getSessionConflictMessage() {
    const message = localStorage.getItem(SESSION_CONFLICT_KEY);
    localStorage.removeItem(SESSION_CONFLICT_KEY);
    return message;
}

/**
 * Check if a GraphQL error is a session expiry/conflict error.
 * Returns the error message if it is, null otherwise.
 */
export function checkSessionError(error) {
    if (!error || !error.errors) return null;

    for (const err of error.errors) {
        if (err.errorType === 'SessionExpired') {
            return err.message || 'Session expired';
        }
    }
    return null;
}

/**
 * Handle session termination — clear session, set conflict message, redirect to login.
 * Call this when a SessionExpired error is detected.
 */
export function handleSessionTermination(history, message) {
    const conflictMessage = message === 'Session has been invalidated'
        ? 'Your session has been terminated because your account was logged in from another location.'
        : message || 'Your session has expired. Please log in again.';

    clearSessionToken();
    setSessionConflict(conflictMessage);

    // Clear user cookies
    const cookies = new Cookies();
    cookies.remove('user_token', { path: '/' });
    cookies.remove('singpass_token', { path: '/' });
    cookies.remove('user-language', { path: '/' });

    // Redirect to login
    if (history) {
        history.push('/login');
    } else {
        window.location.href = '/login';
    }
}

/**
 * Wrapper for API.graphql that automatically handles session errors.
 * Use this instead of API.graphql directly for protected calls.
 *
 * Usage:
 *   import { secureGraphql } from '../infrastructure/SessionManager';
 *   const response = await secureGraphql(history, getUserProgressLambda, { userID: '...' });
 */
export async function secureGraphql(history, query, variables = {}) {
    try {
        const response = await API.graphql(graphqlOperation(query, variables));

        // Check for inline errors in the response
        if (response.errors) {
            const sessionError = checkSessionError(response);
            if (sessionError) {
                handleSessionTermination(history, sessionError);
                return null;
            }
        }

        return response;
    } catch (error) {
        // GraphQL errors from AppSync come as thrown errors
        const sessionError = checkSessionError(error);
        if (sessionError) {
            handleSessionTermination(history, sessionError);
            return null;
        }
        throw error;
    }
}


/**
 * Session heartbeat — keeps the session alive while user is active.
 * Sends a lightweight validateSession call every HEARTBEAT_INTERVAL_MS
 * as long as user activity (mouse/keyboard) is detected.
 * 
 * Call startSessionHeartbeat() after login. It automatically stops
 * when the session token is cleared.
 */
let heartbeatTimer = null;
let lastUserActivity = Date.now();

function onUserActivity() {
    lastUserActivity = Date.now();
}

async function sendHeartbeat() {
    const token = getSessionToken();
    if (!token) {
        // Token is missing (possibly cleared by another portal) — trigger logout
        stopSessionHeartbeat();
        const isAdminPath = window.location.pathname.startsWith('/admin');
        setSessionConflict('Your session has expired. Please log in again.');
        window.location.href = isAdminPath ? '/admin/login' : '/login';
        return;
    }

    // Only send heartbeat if user was active in the last interval
    if (Date.now() - lastUserActivity > HEARTBEAT_INTERVAL_MS) {
        return; // User is idle, don't renew session
    }

    try {
        await API.graphql(graphqlOperation(validateSession, { sessionToken: token, readOnly: false }));
    } catch (error) {
        // If session expired, the next actual API call will handle the redirect
    }
}

export function startSessionHeartbeat() {
    stopSessionHeartbeat(); // Clear any existing heartbeat
    lastUserActivity = Date.now();

    // Listen for user activity
    window.addEventListener('mousemove', onUserActivity);
    window.addEventListener('keydown', onUserActivity);
    window.addEventListener('click', onUserActivity);
    window.addEventListener('scroll', onUserActivity);

    // Start periodic heartbeat
    heartbeatTimer = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);
}

export function stopSessionHeartbeat() {
    if (heartbeatTimer) {
        clearInterval(heartbeatTimer);
        heartbeatTimer = null;
    }
    window.removeEventListener('mousemove', onUserActivity);
    window.removeEventListener('keydown', onUserActivity);
    window.removeEventListener('click', onUserActivity);
    window.removeEventListener('scroll', onUserActivity);
}

/**
 * Fast session invalidation via direct WebSocket to AppSync.
 * Bypasses Amplify's broken subscription implementation.
 */
let sessionSubscription = null;

/**
 * Start WebSocket subscription for session invalidation.
 * @param {string} userID - The logged-in user's ID
 */
export function startSessionSubscription(userID) {
    stopSessionSubscription();

    if (!userID) return;

    console.log('[SessionSubscription] Starting for userID:', userID);

    const API_KEY = 'da2-ro7xo5gdgzdxfkddog4g2yp5ia';
    const HOST = 'opuajghkxjg2jcyod7tftlnxea.appsync-api.ap-southeast-1.amazonaws.com';
    const WS_ENDPOINT = 'wss://opuajghkxjg2jcyod7tftlnxea.appsync-realtime-api.ap-southeast-1.amazonaws.com/graphql';

    try {
        const headerBase64 = btoa(JSON.stringify({ host: HOST, 'x-api-key': API_KEY }));
        const payloadBase64 = btoa(JSON.stringify({}));
        const url = `${WS_ENDPOINT}?header=${headerBase64}&payload=${payloadBase64}`;

        const ws = new WebSocket(url, ['graphql-ws']);
        let keepAliveTimer = null;

        ws.onopen = () => {
            ws.send(JSON.stringify({ type: 'connection_init' }));
        };

        ws.onmessage = (event) => {
            const data = JSON.parse(event.data);

            switch (data.type) {
                case 'connection_ack':
                    ws.send(JSON.stringify({
                        id: '1',
                        type: 'start',
                        payload: {
                            data: JSON.stringify({
                                query: 'subscription { onSessionInvalidated { userID reason timestamp } }'
                            }),
                            extensions: {
                                authorization: { host: HOST, 'x-api-key': API_KEY }
                            }
                        }
                    }));
                    break;

                case 'start_ack':
                    console.log('[SessionSubscription] WebSocket active');
                    break;

                case 'ka':
                    break;

                case 'data':
                    const eventData = data.payload && data.payload.data && data.payload.data.onSessionInvalidated;
                    console.log('[SessionSubscription] Event received:', eventData);
                    if (eventData && eventData.userID === userID) {
                        ws.close();
                        const message = eventData.reason ||
                            'Your session has been terminated because your account was logged in from another location.';
                        clearSessionToken();
                        const cookies = new Cookies();
                        cookies.remove('user_token', { path: '/' });
                        cookies.remove('singpass_token', { path: '/' });
                        cookies.remove('user-language', { path: '/' });
                        sessionStorage.removeItem('mfaVerified');
                        setSessionConflict(message);
                        import('aws-amplify').then(({ Auth }) => {
                            Auth.signOut().catch(() => {});
                        }).catch(() => {});
                        const isAdminPath = window.location.pathname.startsWith('/admin');
                        window.location.href = isAdminPath ? '/admin/login' : '/login';
                    }
                    break;

                case 'error':
                    console.warn('[SessionSubscription] Error:', data.payload);
                    break;

                default:
                    break;
            }
        };

        ws.onerror = () => {
            console.error('[SessionSubscription] Connection error');
        };

        ws.onclose = () => {
            if (keepAliveTimer) clearInterval(keepAliveTimer);
        };

        keepAliveTimer = setInterval(() => {
            if (ws.readyState !== WebSocket.OPEN) {
                clearInterval(keepAliveTimer);
            }
        }, 60000);

        sessionSubscription = {
            unsubscribe: () => {
                if (keepAliveTimer) clearInterval(keepAliveTimer);
                if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
                    ws.close();
                }
            }
        };
    } catch (err) {
        console.error('[SessionSubscription] Failed:', err);
    }
}

/**
 * Stop session invalidation polling.
 */
export function stopSessionSubscription() {
    if (sessionSubscription) {
        sessionSubscription.unsubscribe();
        sessionSubscription = null;
    }
}
